import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import ToolLogo from '../ui/ToolLogo';
import { LogoSlug } from '../../data/logos';

/** Charyx Labs brand palette, taken from charyxlabs.tech. */
const CHARYX_ORANGE = '#d9642c';
const CHARYX_CREAM = '#ead3a0';

const stats = [
  { v: '13+', l: 'businesses supported' },
  { v: '4', l: 'countries served' },
  { v: '4.8★', l: 'average client rating' },
  { v: '~1 wk', l: 'to first automation live' },
];

interface Pillar {
  title: string;
  copy: string;
  tools: LogoSlug[];
}

const pillars: Pillar[] = [
  {
    title: 'Automation & Business Transformation',
    copy: 'Turn manual, human-dependent operations into automated systems — CRM setup, lead capture, booking flows, onboarding and voice AI receptionists.',
    tools: ['n8n', 'gohighlevel', 'zapier', 'make', 'twilio'],
  },
  {
    title: 'AI Systems',
    copy: 'Agents, RAG assistants and voice receptionists that take real action — with guardrails, output validation and post-deploy red-teaming.',
    tools: ['anthropic', 'openai', 'googlegemini', 'langchain', 'elevenlabs'],
  },
  {
    title: 'Product Engineering',
    copy: 'Full products when automation is not enough: multi-tenant SaaS, billing, row-level security, and the frontend that ships with it.',
    tools: ['react', 'fastapi', 'python', 'postgresql', 'stripe'],
  },
];

const shipped = [
  {
    name: 'OnOffDeals',
    note: 'Real-estate deal-analysis SaaS — React 18 SPA, async FastAPI, 311 tests, live Stripe billing.',
    href: 'https://onoffdeals.ai',
  },
  {
    name: 'Life Search AI',
    note: 'Phone-first AI memory assistant — on-device OCR (incl. Hindi Devanagari) plus Claude.',
  },
  {
    name: 'DocuMind',
    note: 'Local-first RAG — retrieval runs entirely on-device, no documents leave the machine.',
  },
  {
    name: 'Gurukin',
    note: 'Free learning platform — Claude Certified Developer prep, JLPT N5 Japanese, and a kids’ track in development.',
    href: 'https://gurukin.app',
  },
];

const StudioSection: React.FC = () => {
  const { themeConfig } = useTheme();

  return (
    <section id="studio" className={themeConfig.layout?.sectionStyle}>
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.6 }}
        className="mb-10 max-w-2xl"
      >
        <div className="mb-3 flex items-center gap-2">
          <span className="h-px w-6" style={{ background: `${CHARYX_ORANGE}99` }} />
          <span
            className="font-mono text-xs uppercase tracking-[0.25em]"
            style={{ color: CHARYX_ORANGE }}
          >
            {'// the studio behind the work'}
          </span>
        </div>
        <h2 className="font-display text-3xl font-bold leading-tight text-ink-main sm:text-4xl">
          I build as <span className="gradient-text">Charyx Labs</span>
        </h2>
        <p className="mt-3 text-lg text-ink-soft">
          Bigger builds run through Charyx Labs, the AI product studio I founded
          and own. It designs, codes, ships and operates production software
          instead of just consulting.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.6 }}
        className="n8n-node overflow-hidden p-0"
        style={{ borderColor: `${CHARYX_ORANGE}40` }}
      >
        {/* brand header */}
        <div
          className="flex flex-col gap-4 border-b p-6 sm:flex-row sm:items-center sm:justify-between"
          style={{
            borderColor: `${CHARYX_ORANGE}33`,
            background: `linear-gradient(120deg, ${CHARYX_ORANGE}1f, transparent 65%)`,
          }}
        >
          <div className="flex items-center gap-4">
            <img
              src="/assets/logos/charyxlabs.svg"
              alt="Charyx Labs"
              width={52}
              height={52}
              className="shrink-0 rounded-xl"
              style={{ width: 52, height: 52, boxShadow: `0 0 30px -8px ${CHARYX_ORANGE}` }}
            />
            <div>
              <p className="font-display text-xl font-bold text-ink-main">
                Charyx Labs
              </p>
              <p className="font-mono text-xs" style={{ color: CHARYX_CREAM }}>
                AI Product Studio &amp; Business Transformation Partner
              </p>
            </div>
          </div>
          <a
            href="https://charyxlabs.tech"
            target="_blank"
            rel="noopener noreferrer"
            className="neon-btn-ghost shrink-0"
            style={{
              color: CHARYX_ORANGE,
              borderColor: `${CHARYX_ORANGE}66`,
              background: `${CHARYX_ORANGE}12`,
            }}
          >
            charyxlabs.tech
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M7 17 17 7M9 7h8v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>

        {/* stats */}
        <div className="grid grid-cols-2 divide-canvas-border/60 border-b border-canvas-border/60 sm:grid-cols-4 sm:divide-x">
          {stats.map((s) => (
            <div key={s.l} className="px-5 py-5 text-center">
              <div className="font-display text-2xl font-bold text-ink-main">
                {s.v}
              </div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-wide text-ink-muted">
                {s.l}
              </div>
            </div>
          ))}
        </div>

        {/* pillars */}
        <div className="grid grid-cols-1 gap-px bg-canvas-border/50 md:grid-cols-3">
          {pillars.map((p) => (
            <div key={p.title} className="bg-canvas-node p-6">
              <h3 className="font-display text-base font-semibold text-ink-main">
                {p.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-ink-soft">
                {p.copy}
              </p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {p.tools.map((t) => (
                  <ToolLogo key={t} slug={t} showLabel={false} size={18} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* shipped */}
        <div className="p-6">
          <p className="mb-4 font-mono text-[10px] uppercase tracking-widest text-ink-muted">
            shipped through the studio
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {shipped.map((s) => {
              const body = (
                <>
                  <div className="mb-1 flex items-center gap-2">
                    <span
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ background: CHARYX_ORANGE, boxShadow: `0 0 8px ${CHARYX_ORANGE}` }}
                    />
                    <span className="font-display text-sm font-semibold text-ink-main">
                      {s.name}
                    </span>
                    {s.href && (
                      <span className="font-mono text-[10px] text-neon-cyan">↗</span>
                    )}
                  </div>
                  <p className="text-xs leading-relaxed text-ink-soft">{s.note}</p>
                </>
              );

              return s.href ? (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-xl border border-canvas-border bg-canvas-deep/60 p-4 transition-colors hover:border-neon-cyan/50"
                >
                  {body}
                </a>
              ) : (
                <div
                  key={s.name}
                  className="rounded-xl border border-canvas-border bg-canvas-deep/60 p-4"
                >
                  {body}
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default StudioSection;
